import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Loader2, Megaphone } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// "We're on LytePress" — a square graphic a shop can post to Instagram or WhatsApp status.
// Rendered to PNG with canvas so there's no extra dependency to install.
const BRAND = "#0f2a1a";
const GREEN = "#16a34a";

export const ShopPromoCard = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    api.get("/my/shop-link").then(({ data }) => setLink(data)).catch(() => {});
  }, []);

  const url = link?.prettyUrl || link?.shareUrl;
  if (!link?.verified || !url) return null;

  const download = async () => {
    setBusy(true);
    try {
      const size = 1080;
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = BRAND; ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = GREEN; ctx.fillRect(0, 0, size, 14);

      ctx.fillStyle = "#a7d8b8"; ctx.font = "600 34px Arial"; ctx.textAlign = "center";
      ctx.fillText("NOW ON LYTEPRESS", size / 2, 150);

      ctx.fillStyle = "#ffffff"; ctx.font = "800 76px Arial";
      const name = (user?.businessName || "Our shop").slice(0, 26);
      ctx.fillText(name, size / 2, 265);

      ctx.fillStyle = "#ffffff"; ctx.font = "400 38px Arial";
      ctx.fillText("Our deals are on the map", size / 2, 335);

      // QR from the rendered SVG so the code always matches what's on screen
      const svg = qrRef.current?.querySelector("svg");
      if (svg) {
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" });
        const img = new Image();
        const objUrl = URL.createObjectURL(blob);
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = objUrl; });
        const qs = 460;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect((size - qs - 48) / 2, 390, qs + 48, qs + 48);
        ctx.drawImage(img, (size - qs) / 2, 414, qs, qs);
        URL.revokeObjectURL(objUrl);
      }

      ctx.fillStyle = "#ffffff"; ctx.font = "700 40px Arial";
      ctx.fillText("Scan to see what's on today", size / 2, 950);
      ctx.fillStyle = "#a7d8b8"; ctx.font = "400 32px Arial";
      ctx.fillText(url.replace(/^https?:\/\//, ""), size / 2, 1005);

      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `lytepress-${(user?.businessName || "shop").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      a.click();
      toast({ title: t("promo.saved"), description: t("promo.savedNote") });
    } catch {
      toast({ title: t("acc.pleaseTryAgain") });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="shop-promo-card">
      <p className="text-base font-extrabold text-foreground flex items-center gap-1.5">
        <Megaphone size={16} className="text-[#16a34a]" /> {t("promo.title")}
      </p>
      <p className="text-sm text-muted-foreground mt-1">{t("promo.sub")}</p>

      <div className="mt-4 flex items-center gap-4">
        <div ref={qrRef} className="bg-white p-2 rounded-lg shrink-0">
          <QRCodeSVG value={url} size={96} level="M" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground truncate">{user?.businessName}</p>
          <p className="text-xs text-muted-foreground truncate">{url.replace(/^https?:\/\//, "")}</p>
          <button onClick={download} disabled={busy} data-testid="download-promo-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-4 py-2.5 transition-colors">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} {t("promo.cta")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopPromoCard;
